<template>
  <div class="ui-page" :class="{ 'ui-page--with-assistant': aiOpen }" tabindex="-1">
    <LeadFlowFilters
      :value="filters"
      :countries="countryOptions"
      :adv-sources="advSourceOptions"
      :tour-operators="tourOperatorOptions"
      :domains="domainOptions"
      :managers="managerOptions"
      :managers-loading="managersLoading"
      :managers-loading-more="managersLoadingMore"
      :manager-has-more="managerHasMore"
      :manager-error="managerError"
      :show-manager="scope.can_filter_by_manager"
      :show-advanced-filters="scope.can_use_advanced_filters"
      @change="onFilterChange"
      @clear="clearFilters"
      @manager-open="onManagerOpen"
      @manager-search="onManagerSearch"
      @manager-load-more="loadMoreManagers"
      @manager-retry="retryManagers"
    >
      <template #actions>
        <AppIconButton label="Ask AI" tooltip-placement="left" :aria-expanded="String(aiOpen)" @click="aiOpen = !aiOpen">
          <i class="pi pi-sparkles" aria-hidden="true" />
        </AppIconButton>
      </template>
    </LeadFlowFilters>

    <LeadNewLeadTakeControl
      v-if="canTakeNewLead"
      :load-state="loadNewLeads"
      :take-lead="takeNewLead"
      @assigned="handleTakenLead"
    />

    <LeadFlowBoard
      ref="board"
      :columns="columns"
      :filters="filterSnapshot"
      :generation="boardGeneration"
      :load-cards="loadColumnCards"
      :load-new-leads="loadNewLeads"
      :show-new-leads-backlog="showNewLeadsBacklog"
      :scope="scope"
      :current-user-id="currentUserId"
      :loading="columnsLoading"
      :error="columnsError"
      @open="openLead"
      @retry="retryColumns"
      @metadata-refresh="refreshColumnMetadata"
    />

    <LeadFlowModal
      v-if="selectedModalId"
      :key="selectedModalId"
      :lead-id="selectedModalId"
      :ai-open="aiOpen"
      @ask-ai="aiOpen = !aiOpen"
      @board-reconcile="reconcileBoardChange"
      @close="closeRouteModal"
    />
    <KnowledgeChatPanel :open="aiOpen" @minimize="aiOpen = false" />
  </div>
</template>

<script>
import {
  getClientsTourOperators,
  getCountries,
  getLeadFlowNewLeads,
  getLeadFlowTrackerCards,
  getLeadFlowTrackerColumns,
  getLeadFlowTrackerLeadsSummary,
  getLeadFlowTrackerManagers,
  getLeadsAdvSources,
  postLeadFlowTakeNewLead
} from '@/http'
import KnowledgeChatPanel from '@/components/knowledge-ai/ChatPanel.vue'
import AppIconButton from '@/components/AppIconButton.vue'
import LeadFlowBoard from '@/views/crm/task-tracker/LeadFlowBoard.vue'
import LeadFlowFilters from '@/views/crm/task-tracker/LeadFlowFilters.vue'
import LeadFlowModal from '@/views/crm/task-tracker/LeadFlowModal.vue'
import LeadNewLeadTakeControl from '@/views/crm/task-tracker/LeadNewLeadTakeControl.vue'
import { modalRoute } from '@/views/crm/modalRoute'
import managementGuidancePolling from '@/views/crm/task-tracker/managementGuidancePolling'

export default {
  name: 'LeadFlowTrackerIndex',
  mixins: [modalRoute('lead'), managementGuidancePolling],
  components: {
    AppIconButton,
    KnowledgeChatPanel,
    LeadFlowBoard,
    LeadFlowFilters,
    LeadFlowModal,
    LeadNewLeadTakeControl
  },
  data() {
    return {
      aiOpen: false,
      filters: {
        search: '',
        country: null,
        activity: [],
        manager: null,
        requestDates: [],
        confirmationDates: [],
        travelDates: [],
        advSources: [],
        tourOperators: [],
        domains: []
      },
      filterSnapshot: Object.freeze({}),
      boardGeneration: 0,
      columnsRequestGeneration: 0,
      columnsLoading: false,
      columnsError: null,
      columns: [],
      countryOptions: [],
      advSourceOptions: [],
      tourOperatorOptions: [],
      domainOptions: [],
      filterOptionsLoaded: false,
      filterOptionsLoading: false,
      managerOptions: [],
      managersLoading: false,
      managersLoadingMore: false,
      managersLoaded: false,
      managerSearch: '',
      managerNextCursor: null,
      managerHasMore: false,
      managerError: null,
      managerRequestGeneration: 0,
      scope: {
        mode: 'all',
        can_filter_by_manager: false,
        can_use_advanced_filters: false,
        selected_manager: null
      },
      scopeLoaded: false,
      searchTimer: null,
      managerSearchTimer: null
    }
  },
  computed: {
    canTakeNewLead() {
      return this.scopeLoaded && !this.scope.can_filter_by_manager
    },
    showNewLeadsBacklog() {
      return this.scopeLoaded && this.scope.can_filter_by_manager
    },
    currentUserId() {
      const userId = Number(this.$store.state.userData.id)
      return Number.isInteger(userId) && userId > 0 ? userId : null
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(query) {
        const filters = this.filtersFromQuery(query)
        if (this.filterSignature(filters) === this.filterSignature(this.filters)) return
        this.filters = filters
        clearTimeout(this.searchTimer)
        this.applyFilters()
      }
    }
  },
  created() {
    this.filters = this.filtersFromQuery(this.$route.query)
    this.syncFilterRoute()
    this.filterSnapshot = this.createFilterSnapshot(this.filters)
    this.loadCountries()
    this.applyFilters()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
    clearTimeout(this.managerSearchTimer)
    this.columnsRequestGeneration += 1
    this.managerRequestGeneration += 1
  },
  methods: {
    guidanceRows() {
      const board = this.$refs.board
      return board ? board.guidanceRows() : []
    },
    onFilterChange(filters, changedFilter) {
      let nextFilters = filters
      if (changedFilter === 'search' && (filters.search || '').trim()) {
        nextFilters = this.filtersFromQuery({ search: filters.search })
      }
      this.filters = {
        ...nextFilters,
        activity: [...(nextFilters.activity || [])],
        requestDates: [...(nextFilters.requestDates || [])],
        confirmationDates: [...(nextFilters.confirmationDates || [])],
        travelDates: [...(nextFilters.travelDates || [])],
        advSources: [...(nextFilters.advSources || [])],
        tourOperators: [...(nextFilters.tourOperators || [])],
        domains: [...(nextFilters.domains || [])]
      }
      this.syncFilterRoute()

      clearTimeout(this.searchTimer)
      if (changedFilter === 'search') {
        this.columnsRequestGeneration += 1
        this.columns = []
        this.columnsLoading = true
        this.columnsError = null
        this.searchTimer = setTimeout(() => this.applyFilters(), 400)
      } else {
        this.applyFilters()
      }
    },
    clearFilters() {
      clearTimeout(this.searchTimer)
      this.filters = {
        search: '',
        country: null,
        activity: [],
        manager: null,
        requestDates: [],
        confirmationDates: [],
        travelDates: [],
        advSources: [],
        tourOperators: [],
        domains: []
      }
      this.syncFilterRoute()
      this.applyFilters()
    },
    createFilterSnapshot(filters) {
      return Object.freeze(this.filterParams(filters))
    },
    applyFilters() {
      const snapshot = this.createFilterSnapshot(this.filters)
      const generation = this.columnsRequestGeneration + 1
      this.filterSnapshot = snapshot
      this.columnsRequestGeneration = generation
      this.boardGeneration += 1
      this.columns = []
      this.loadColumns(snapshot, generation)
    },
    async loadColumns(snapshot, generation) {
      this.columnsLoading = true
      this.columnsError = null
      try {
        const { data } = await getLeadFlowTrackerColumns(snapshot)
        if (generation !== this.columnsRequestGeneration) return

        const scope = data.scope || {
          mode: 'own',
          can_filter_by_manager: false,
          can_use_advanced_filters: false,
          selected_manager: null
        }
        this.scope = scope
        this.scopeLoaded = true

        if (!scope.can_filter_by_manager && snapshot.manager) {
          this.filters = {
            ...this.filters,
            manager: null
          }
          this.syncFilterRoute()
          this.applyFilters()
          return
        }

        if (!scope.can_use_advanced_filters && this.hasAdvancedFilters(this.filters)) {
          this.filters = this.withoutAdvancedFilters(this.filters)
          this.syncFilterRoute()
          this.applyFilters()
          return
        }

        if (scope.can_filter_by_manager && scope.selected_manager) {
          this.addManagerOption(scope.selected_manager)
        }
        if (scope.can_use_advanced_filters) this.ensureFilterOptionsLoaded()
        this.columns = data.columns || []
      } catch (error) {
        if (generation === this.columnsRequestGeneration) {
          this.columnsError = error
          this.columns = []
        }
      } finally {
        if (generation === this.columnsRequestGeneration) this.columnsLoading = false
      }
    },
    loadColumnCards(params) {
      return getLeadFlowTrackerCards(params)
    },
    loadNewLeads() {
      const params = this.filterSnapshot.search ? { search: this.filterSnapshot.search } : undefined
      return getLeadFlowNewLeads(params)
    },
    takeNewLead() {
      return postLeadFlowTakeNewLead()
    },
    handleTakenLead(data) {
      const lead = data && data.lead ? data.lead : {}
      const leadId = Number(lead.id)
      if (!Number.isInteger(leadId) || leadId <= 0) return
      const board = this.$refs.board
      if (board) {
        board.handleNewLeadAssigned({
          ...data,
          id: leadId,
          manager: lead.manager || null,
          source: 'http'
        })
      }
      this.openLead(leadId)
    },
    async refreshColumnMetadata() {
      const snapshot = this.filterSnapshot
      const generation = this.columnsRequestGeneration
      try {
        const { data } = await getLeadFlowTrackerColumns(snapshot)
        if (generation !== this.columnsRequestGeneration || snapshot !== this.filterSnapshot) return

        const scope = data.scope || {
          mode: 'own',
          can_filter_by_manager: false,
          can_use_advanced_filters: false,
          selected_manager: null
        }
        this.scope = scope
        this.scopeLoaded = true

        if (!scope.can_filter_by_manager && snapshot.manager) {
          this.filters = {
            ...this.filters,
            manager: null
          }
          this.syncFilterRoute()
          this.applyFilters()
          return
        }
        if (!scope.can_use_advanced_filters && this.hasAdvancedFilters(this.filters)) {
          this.filters = this.withoutAdvancedFilters(this.filters)
          this.syncFilterRoute()
          this.applyFilters()
          return
        }

        if (scope.can_filter_by_manager && scope.selected_manager) {
          this.addManagerOption(scope.selected_manager)
        }
        if (scope.can_use_advanced_filters) this.ensureFilterOptionsLoaded()
        this.columns = data.columns || []
      } catch (_) {}
    },
    async reconcileBoardChange(change = {}) {
      if (change.management_guidance_changed) {
        this.applyFilters()
        return
      }
      const delta = change.tracker_delta || {}
      const card = delta.card || null
      const board = this.$refs.board
      const cardUpdated = Boolean(card && board && board.updateCard(card))
      const columnCodes = this.affectedColumnCodes(change)
      const previousColumn = delta.previous && delta.previous.column
      const currentColumn = delta.current && delta.current.column
      const stayedInColumn = previousColumn && previousColumn === currentColumn
      if (board && columnCodes.length && !(cardUpdated && stayedInColumn)) {
        await board.refreshColumns(columnCodes)
      }
      await this.refreshColumnMetadata()
    },
    affectedColumnCodes(change) {
      const delta = change.tracker_delta || {}
      const previousDelta = delta.previous || {}
      const currentDelta = delta.current || {}
      const directCodes = [previousDelta.column, currentDelta.column].filter(Boolean)

      const stages = [
        change.previous_stage && change.previous_stage.code,
        change.current_stage && change.current_stage.code
      ].filter(Boolean)
      const stageCodes = this.columns
        .filter(column => {
          return stages.includes(column.code)
            || (column.stage_codes || []).some(stage => stages.includes(stage))
        })
        .map(column => column.code)
      return [...new Set(directCodes.concat(stageCodes))]
    },
    retryColumns() {
      clearTimeout(this.searchTimer)
      this.applyFilters()
    },
    async loadCountries() {
      try {
        const { data } = await getCountries()
        this.countryOptions = Array.isArray(data) ? data : []
      } catch (_) {
        this.countryOptions = []
      }
    },
    async ensureFilterOptionsLoaded() {
      if (this.filterOptionsLoaded || this.filterOptionsLoading) return
      this.filterOptionsLoading = true
      if (!this.managersLoaded && !this.managersLoading) this.loadManagers(true)
      const [advSources, tourOperators, summary] = await Promise.allSettled([
        getLeadsAdvSources(),
        getClientsTourOperators(),
        getLeadFlowTrackerLeadsSummary()
      ])
      this.advSourceOptions = []
      if (advSources.status === 'fulfilled') {
        this.advSourceOptions = this.optionResults(advSources.value.data)
      }
      this.tourOperatorOptions = []
      if (tourOperators.status === 'fulfilled') {
        this.tourOperatorOptions = this.optionResults(tourOperators.value.data)
      }
      this.domainOptions = []
      if (summary.status === 'fulfilled') {
        this.domainOptions = summary.value.data.domains || []
      }
      this.filterOptionsLoaded = true
      this.filterOptionsLoading = false
    },
    optionResults(data) {
      if (Array.isArray(data)) return data
      return data && Array.isArray(data.results) ? data.results : []
    },
    onManagerOpen() {
      if (!this.managersLoaded && !this.managersLoading) this.loadManagers(true)
    },
    onManagerSearch(search) {
      this.managerSearch = (search || '').trim()
      clearTimeout(this.managerSearchTimer)

      const generation = this.managerRequestGeneration + 1
      this.managerRequestGeneration = generation
      this.managerSearchTimer = setTimeout(() => this.loadManagers(true, generation), 400)
    },
    async loadManagers(reset, scheduledGeneration) {
      if (!reset && (this.managersLoading || this.managersLoadingMore || !this.managerHasMore)) return

      const nextGeneration = reset ? this.managerRequestGeneration + 1 : this.managerRequestGeneration
      const generation = scheduledGeneration || nextGeneration
      this.managerRequestGeneration = generation

      if (reset) {
        this.managersLoading = true
        this.managersLoadingMore = false
        this.managerNextCursor = null
        this.managerHasMore = false
        this.managerError = null
        this.managerOptions = this.selectedManagerOptions()
      } else {
        this.managersLoadingMore = true
        this.managerError = null
      }

      try {
        const params = { page_size: 50 }
        if (this.managerSearch) params.search = this.managerSearch
        if (!reset && this.managerNextCursor) params.cursor = this.managerNextCursor

        const { data } = await getLeadFlowTrackerManagers(params)
        if (generation !== this.managerRequestGeneration) return

        const currentOptions = reset ? this.selectedManagerOptions() : this.managerOptions
        this.managerOptions = this.deduplicateManagers(currentOptions.concat(data.results || []))
        this.managerNextCursor = data.next_cursor || null
        this.managerHasMore = Boolean(data.has_more)
        this.managersLoaded = true
      } catch (error) {
        if (generation === this.managerRequestGeneration) {
          this.managerError = error
          if (reset) this.managersLoaded = false
        }
      } finally {
        if (generation === this.managerRequestGeneration) {
          this.managersLoading = false
          this.managersLoadingMore = false
        }
      }
    },
    loadMoreManagers() {
      this.loadManagers(false)
    },
    retryManagers() {
      this.loadManagers(true)
    },
    selectedManagerOptions() {
      if (!this.filters.manager) return []

      const selectedManager = this.managerOptions.find(manager => manager.id === this.filters.manager)
        || this.scope.selected_manager
      return selectedManager && selectedManager.id === this.filters.manager ? [selectedManager] : []
    },
    addManagerOption(manager) {
      this.managerOptions = this.deduplicateManagers([manager].concat(this.managerOptions))
    },
    deduplicateManagers(managers) {
      const managerIds = new Set()
      return managers.filter(manager => {
        if (!manager || managerIds.has(manager.id)) return false
        managerIds.add(manager.id)
        return true
      })
    },
    normalizeManagerId(manager) {
      const managerId = Number(manager)
      return Number.isInteger(managerId) && managerId > 0 ? managerId : null
    },
    filterParams(filters = this.filters) {
      const params = {}
      const search = (filters.search || '').trim()
      if (search) return { search }
      if (filters.country) params.country = filters.country
      if ((filters.activity || []).length) params.activity = filters.activity.join(',')
      if (filters.manager) params.manager = filters.manager
      this.addRangeParams(params, 'request', filters.requestDates)
      this.addRangeParams(params, 'confirmation', filters.confirmationDates)
      this.addRangeParams(params, 'travel', filters.travelDates)
      if ((filters.advSources || []).length) params.adv_sources = filters.advSources.join(',')
      if ((filters.tourOperators || []).length) params.tour_operators = filters.tourOperators.join(',')
      if ((filters.domains || []).length) params.domains = filters.domains.join(',')
      return params
    },
    addRangeParams(params, prefix, range) {
      if (!Array.isArray(range)) return
      if (range[0]) params[`${prefix}_from`] = range[0]
      if (range[1]) params[`${prefix}_to`] = range[1]
    },
    queryValue(value) {
      return Array.isArray(value) ? value[0] : value
    },
    csvQueryValues(value) {
      return String(this.queryValue(value) || '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
    },
    numericQueryValue(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : value
    },
    dateRangeFromQuery(query, prefix) {
      const from = this.queryValue(query[`${prefix}_from`]) || ''
      const to = this.queryValue(query[`${prefix}_to`]) || ''
      return from || to ? [from, to] : []
    },
    filtersFromQuery(query = {}) {
      return {
        search: String(this.queryValue(query.search) || ''),
        country: this.queryValue(query.country) || null,
        activity: this.csvQueryValues(query.activity),
        manager: this.normalizeManagerId(this.queryValue(query.manager)),
        requestDates: this.dateRangeFromQuery(query, 'request'),
        confirmationDates: this.dateRangeFromQuery(query, 'confirmation'),
        travelDates: this.dateRangeFromQuery(query, 'travel'),
        advSources: this.csvQueryValues(query.adv_sources),
        tourOperators: this.csvQueryValues(query.tour_operators).map(value => this.numericQueryValue(value)),
        domains: this.csvQueryValues(query.domains).map(value => this.numericQueryValue(value))
      }
    },
    filterSignature(filters) {
      return JSON.stringify(this.filterParams(filters))
    },
    hasAdvancedFilters(filters) {
      return Boolean(
        (filters.requestDates || []).length
        || (filters.confirmationDates || []).length
        || (filters.travelDates || []).length
        || (filters.advSources || []).length
        || (filters.tourOperators || []).length
        || (filters.domains || []).length
      )
    },
    withoutAdvancedFilters(filters) {
      return {
        ...filters,
        requestDates: [],
        confirmationDates: [],
        travelDates: [],
        advSources: [],
        tourOperators: [],
        domains: []
      }
    },
    syncFilterRoute() {
      const query = { ...this.$route.query }
      const keys = [
        'search', 'country', 'activity', 'manager',
        'request_from', 'request_to', 'confirmation_from', 'confirmation_to',
        'travel_from', 'travel_to', 'adv_sources', 'tour_operators', 'domains'
      ]
      keys.forEach(key => delete query[key])
      Object.entries(this.filterParams()).forEach(([key, value]) => {
        query[key] = String(value)
      })
      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return
      this.$router.replace({ query }).catch(() => {})
    },
    openLead(payload) {
      const leadId = Number(payload && payload.leadId ? payload.leadId : payload)
      if (!Number.isInteger(leadId) || leadId <= 0) return

      this.openRouteModal(leadId, payload && payload.triggerElement)
    }
  }
}
</script>

<style lang="scss" src="./lead-flow-theme.scss"></style>
